export function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const part = (token || '').split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const parsed = JSON.parse(atob(padded));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch { return null; }
}

/**
 * Splits a raw `custom:tenantId` claim into the individual tenant ids the
 * backend's `requireCaller` will accept in `x-tenant-id`. Mirrors the
 * backend's own comma-split parsing so the two cannot drift: trim each
 * entry, drop empties (guards "a,,b" and trailing/leading commas).
 */
export function tenantIdsFromToken(token: string | null): string[] {
  const claims = decodeJwtPayload(token || '');
  const raw = claims?.['custom:tenantId'];
  if (!raw || typeof raw !== 'string') return [];
  return raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
}

/**
 * Normalises a raw role claim exactly the way platform-app's own `mapRole`
 * does (services/apiService.ts, ~line 845): lowercase, collapse whitespace
 * to `_`. This package cannot import that function (must stay
 * dependency-light, no platform-app coupling), so the logic is copied here.
 * If `mapRole` ever changes its accepted spellings, this must be updated to
 * match or the recorder gate and platform-app's own role check will drift
 * apart.
 */
function normaliseRole(roleVal: any): 'super_admin' | 'admin' | 'employee' | null {
  if (!roleVal) return null;
  const val = String(roleVal).toLowerCase().replace(/\s+/g, '_');
  if (['super_admin', 'superadmin', 'super-admin'].includes(val)) return 'super_admin';
  if (val === 'admin') return 'admin';
  if (['employee', 'staff'].includes(val)) return 'employee';
  return null;
}

/**
 * Client-side gate for SHOWING the overlay only. Every write it makes is
 * re-authorised server-side by requireSuperAdmin — this decides what to render,
 * never what is permitted.
 *
 * Admits:
 *  - `custom:superAdminRole` truthy — the platform-admin pool issues this and
 *    carries no `custom:role` attribute at all.
 *  - `custom:role` normalising to `super_admin` or `admin` — platform-app
 *    signs into the merchant Cognito pool, where a business owner carries
 *    `custom:role: 'admin'`; merchant admins record tutorials too.
 *
 * `employee` / `staff` are explicitly denied, not merely "not matched" —
 * keep the denial branch honest so this can never silently widen to every
 * logged-in user.
 */
export function canRecordTutorials(token: string | null): boolean {
  if (!token) return false;
  const claims = decodeJwtPayload(token);
  if (!claims) return false;

  const sar = claims['custom:superAdminRole'];
  if (sar === true || (typeof sar === 'string' && sar !== '' && sar !== 'false')) return true;

  const role = normaliseRole(claims['custom:role']);
  return role === 'super_admin' || role === 'admin';
}

/** @deprecated Renamed to `canRecordTutorials` — merchant admins record tutorials too now, not just super-admins. Kept as an alias so existing imports keep working. */
export const isTutorialAdmin = canRecordTutorials;
